// Birthdays + work anniversaries.
//
//  - BirthdayPrompt: a one-time "when's your birthday?" modal. Answering OR skipping
//    marks it done server-side, so it never asks again on any device.
//  - CelebrationOverlay: balloons + confetti for the person whose day it is, on the
//    first open that day. "Played today" is stored server-side (celebrated_on), not
//    in localStorage, so it shows once per day even across phone and desktop.
//  - Banner: everyone sees "Today is X's birthday" / "X — 3 years with the team".
//
// "Today" comes from the server (eastern_today()), never the browser clock.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTodaysCelebrations, markCelebrationSeen, setMyBirthday } from '../services/api';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// Days in a month, checked against a leap year so Feb 29 is selectable.
const daysIn = (month) => new Date(2000, month, 0).getDate();

const firstName = (name) => (name || '').trim().split(/\s+/)[0] || name;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const BALLOON_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];
const CONFETTI_COLORS = ['#ef4444', '#f59e0b', '#facc15', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

const KEYFRAMES = `
@keyframes ngs-balloon-rise {
  0%   { transform: translate(0, 0) rotate(-4deg); opacity: 0; }
  8%   { opacity: 1; }
  50%  { transform: translate(var(--sway), -60vh) rotate(4deg); }
  100% { transform: translate(0, -125vh) rotate(-4deg); opacity: 1; }
}
@keyframes ngs-card-pop {
  0%   { transform: scale(0.6); opacity: 0; }
  70%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
`;

function Balloon({ color, left, delay, duration, size, sway }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: -size * 2.2,
        left: `${left}%`,
        width: size,
        animation: `ngs-balloon-rise ${duration}s ease-in ${delay}s forwards`,
        opacity: 0,
        '--sway': `${sway}px`,
      }}
    >
      <svg width={size} height={size * 2.1} viewBox="0 0 40 84" aria-hidden="true">
        <ellipse cx="20" cy="22" rx="18" ry="22" fill={color} />
        <ellipse cx="13" cy="14" rx="4" ry="7" fill="#fff" opacity="0.35" />
        <path d="M17 43 L23 43 L20 47 Z" fill={color} />
        <path d="M20 47 C 14 56, 26 64, 20 72 S 16 80, 20 84" stroke="#9ca3af" strokeWidth="1" fill="none" />
      </svg>
    </div>
  );
}

function ConfettiCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(220, Math.round(width / 5));
    const pieces = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * height,
      w: 6 + Math.random() * 6,
      h: 8 + Math.random() * 8,
      vy: 2 + Math.random() * 3,
      vx: -1 + Math.random() * 2,
      rot: Math.random() * Math.PI,
      vr: -0.15 + Math.random() * 0.3,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    }));

    let frame;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);
      let alive = 0;
      pieces.forEach((p) => {
        p.x += p.vx + Math.sin((elapsed / 600) + p.rot) * 0.6;
        p.y += p.vy;
        p.rot += p.vr;
        // Keep it raining for the first few seconds, then let it fall away.
        if (p.y > height + 20 && elapsed < 4000) {
          p.y = -20;
          p.x = Math.random() * width;
        }
        if (p.y <= height + 20) alive += 1;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.rot)));
        ctx.restore();
      });
      if (alive > 0) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
}

function CelebrationOverlay({ user, mine, onClose }) {
  const reduced = prefersReducedMotion();
  const [balloons] = useState(() => Array.from({ length: 16 }, (_, i) => ({
    color: BALLOON_COLORS[i % BALLOON_COLORS.length],
    left: Math.random() * 92,
    delay: Math.random() * 2.5,
    duration: 6 + Math.random() * 3,
    size: 38 + Math.random() * 26,
    sway: -40 + Math.random() * 80,
  })));

  useEffect(() => {
    const t = setTimeout(onClose, 10000);
    return () => clearTimeout(t);
  }, [onClose]);

  const birthday = mine.find((c) => c.kind === 'birthday');
  const anniversary = mine.find((c) => c.kind === 'anniversary');
  const name = firstName(user.name);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-label="Celebration"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15, 41, 66, 0.55)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <style>{KEYFRAMES}</style>
      {!reduced && <ConfettiCanvas />}
      {!reduced && balloons.map((b, i) => <Balloon key={i} {...b} />)}
      <div
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: '16px',
          padding: '28px 32px',
          maxWidth: '420px',
          width: 'calc(100% - 32px)',
          textAlign: 'center',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          animation: reduced ? 'none' : 'ngs-card-pop 0.6s ease-out',
        }}
      >
        <div style={{ fontSize: '56px', lineHeight: 1 }}>{birthday ? '🎂' : '🎉'}</div>
        {birthday && (
          <h2 style={{ margin: '14px 0 6px', color: '#0f2942', fontSize: '26px' }}>
            Happy Birthday, {name}!
          </h2>
        )}
        {anniversary && (
          <h2 style={{ margin: birthday ? '6px 0' : '14px 0 6px', color: '#0f2942', fontSize: birthday ? '18px' : '26px' }}>
            {birthday ? 'And happy work anniversary too!' : `Happy Work Anniversary, ${name}!`}
          </h2>
        )}
        <p style={{ margin: '10px 0 0', color: '#4b5563', fontSize: '15px' }}>
          {anniversary
            ? `${plural(anniversary.years, 'year')} with North Georgia Surveying. Thank you for everything you do!`
            : 'Everyone at North Georgia Surveying hopes you have a great day!'}
        </p>
        <p style={{ margin: '18px 0 0', color: '#9ca3af', fontSize: '12px' }}>Tap anywhere to continue</p>
      </div>
    </div>
  );
}

function BirthdayPrompt({ onDone }) {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (skip) => {
    setSaving(true);
    setError('');
    try {
      await setMyBirthday(skip ? { skip: true } : { month: Number(month), day: Number(day) });
      onDone(!skip);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save. Please try again.');
      setSaving(false);
    }
  };

  const selectStyle = {
    flex: 1,
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#111827',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        role="dialog"
        aria-label="Your birthday"
        style={{
          background: '#fff',
          borderRadius: '14px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ fontSize: '40px', textAlign: 'center' }}>🎂</div>
        <h2 style={{ margin: '8px 0', textAlign: 'center', color: '#0f2942', fontSize: '22px' }}>
          When's your birthday?
        </h2>
        <p style={{ margin: '0 0 18px', color: '#4b5563', fontSize: '14px', textAlign: 'center' }}>
          We'd like to celebrate with you! Just the month and day. We don't need the year.
          The team will see a 🎂 on your day.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select
            value={month}
            onChange={(e) => {
              const m = e.target.value;
              setMonth(m);
              if (m && day && Number(day) > daysIn(Number(m))) setDay('');
            }}
            style={selectStyle}
            aria-label="Month"
          >
            <option value="">Month</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={day} onChange={(e) => setDay(e.target.value)} style={{ ...selectStyle, flex: '0 0 90px' }} aria-label="Day">
            <option value="">Day</option>
            {Array.from({ length: month ? daysIn(Number(month)) : 31 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
        </div>
        {error && <p style={{ color: '#dc2626', fontSize: '13px', margin: '10px 0 0' }}>{error}</p>}
        <button
          onClick={() => submit(false)}
          disabled={!month || !day || saving}
          style={{
            width: '100%',
            marginTop: '18px',
            padding: '12px',
            fontSize: '16px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: !month || !day ? '#9ca3af' : '#0f2942',
            color: '#fff',
            cursor: !month || !day || saving ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => submit(true)}
          disabled={saving}
          style={{
            width: '100%',
            marginTop: '8px',
            padding: '8px',
            fontSize: '13px',
            border: 'none',
            background: 'transparent',
            color: '#6b7280',
            cursor: 'pointer',
          }}
        >
          I'd rather not say
        </button>
      </div>
    </div>
  );
}

function CelebrationBanner({ user, date, celebrations }) {
  const key = `celebrationBannerDismissed_${user.id}_${date}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  });
  if (dismissed || !celebrations.length) return null;

  const who = (c) => (c.member_id === user.id ? 'you' : firstName(c.name));
  const birthdays = celebrations.filter((c) => c.kind === 'birthday');
  const anniversaries = celebrations.filter((c) => c.kind === 'anniversary');
  const parts = [];
  if (birthdays.length) {
    const names = birthdays.map(who);
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
    parts.push(`🎂 Happy birthday, ${list}!`);
  }
  anniversaries.forEach((c) => {
    parts.push(`🎉 ${c.member_id === user.id ? 'You' : firstName(c.name)}: ${plural(c.years, 'year')} with the team!`);
  });

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(key, '1');
    } catch (e) {
      // Private mode: dismissal just won't stick across reloads.
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(90deg, #fdf2f8, #fef3c7, #ecfdf5)',
        borderBottom: '2px solid #f472b6',
        padding: '10px 40px 10px 20px',
        position: 'relative',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 600,
        color: '#831843',
      }}
    >
      {parts.join('  ·  ')}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          border: 'none',
          background: 'transparent',
          fontSize: '18px',
          color: '#9d174d',
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// `blocked`: another modal (supply check, toolbox talk) is up — hold the birthday
// question until it closes so the two don't stack.
export default function Celebrations({ blocked }) {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [promptAnswered, setPromptAnswered] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getTodaysCelebrations();
      setData(res.data);
      if (res.data.show_mine) {
        setShowOverlay(true);
        // Mark it played straight away so a second tab or device doesn't replay it.
        markCelebrationSeen().catch(() => {});
      }
    } catch (err) {
      // Offline or older backend — just no celebrations.
    }
  }, []);

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id, load]);

  const closeOverlay = useCallback(() => setShowOverlay(false), []);

  if (!user) return null;

  const needsPrompt = !promptAnswered && !user.birthday_prompt_done && !user.birth_month;

  const handlePromptDone = (saved) => {
    setPromptAnswered(true);
    if (refreshUser) refreshUser();
    // If they just told us today is their birthday, celebrate right now.
    if (saved) load();
  };

  return (
    <>
      {data && <CelebrationBanner user={user} date={data.date} celebrations={data.celebrations || []} />}
      {needsPrompt && !blocked && !showOverlay && <BirthdayPrompt onDone={handlePromptDone} />}
      {showOverlay && data && (
        <CelebrationOverlay user={user} mine={data.mine || []} onClose={closeOverlay} />
      )}
    </>
  );
}
